import React, { useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Building2,
  Calendar,
  MessageSquare,
  Settings,
  ImageIcon,
  FileText,
  CreditCard,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  QrCode,
  Video,
  Award,
  Ticket,
  MessageCircle,
  BarChart3,
  UserCircle,
  Shield,
  Bot,
  Phone,
  Play,
  UserCheck,
  Folder,
  BookOpen,
  Mail,
  FolderOpen,
  FileQuestion,
  Gift,
  Radio,
  Crown
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
  { icon: Shield, label: 'Gobernanza', path: '/admin/governance' },
  { icon: Crown, label: 'Directores', path: '/admin/institucional' },
  { icon: Building2, label: 'Proveedores', path: '/admin/proveedores' },
  { icon: Bot, label: 'UPIM IA', path: '/admin/upim-ia' },
  { icon: Phone, label: 'Inbox WhatsApp', path: '/admin/omnichannel' },
  { icon: BookOpen, label: 'Academia', path: '/admin/academia' },
  { icon: FileQuestion, label: 'Exámenes', path: '/admin/examenes' },
  { icon: Award, label: 'Reconocimientos', path: '/admin/reconocimientos' },
  { icon: Gift, label: 'Badges', path: '/admin/badges' },
  { icon: Mail, label: 'Email Marketing', path: '/admin/email-marketing' },
  { icon: FolderOpen, label: 'Biblioteca Recursos', path: '/admin/biblioteca' },
  { icon: Play, label: 'Biblioteca Videos', path: '/admin/videos' },
  { icon: Radio, label: 'Lives / Streaming', path: '/admin/lives' },
  { icon: UserCheck, label: 'Ponentes', path: '/admin/ponentes' },
  { icon: Folder, label: 'Materiales', path: '/admin/materiales' },
  { icon: UserCircle, label: 'CRM', path: '/admin/crm' },
  { icon: Users, label: 'Afiliados', path: '/admin/afiliados' },
  { icon: Shield, label: 'Verificaciones', path: '/admin/verificaciones' },
  { icon: ImageIcon, label: 'Carruseles', path: '/admin/carruseles' },
  { icon: Calendar, label: 'Eventos', path: '/admin/eventos' },
  { icon: Video, label: 'Webinars', path: '/admin/webinars' },
  { icon: Award, label: 'Certificaciones', path: '/admin/certificaciones' },
  { icon: Ticket, label: 'Cupones', path: '/admin/cupones' },
  { icon: MessageCircle, label: 'Notificaciones', path: '/admin/notificaciones' },
  { icon: QrCode, label: 'Check-in', path: '/admin/checkin' },
  { icon: MessageSquare, label: 'Mensajes', path: '/admin/mensajes' },
  { icon: CreditCard, label: 'Pagos', path: '/admin/pagos' },
  { icon: Settings, label: 'Configuración', path: '/admin/configuracion' },
];

const AdminLayout = ({ children }) => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Para desarrollo, permitimos acceso al admin
  // En producción, verificar rol de admin
  const isAdmin = true; // user?.role === 'admin' || user?.role === 'superadmin';

  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      logout();
      window.location.href = '/login';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Comentado para desarrollo - descomentar en producción
  // if (!isAuthenticated || !isAdmin) {
  //   return <Navigate to="/login" replace />;
  // }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-gray-900 text-white transform transition-transform duration-200 flex flex-col
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800 flex-shrink-0">
          <Link to="/admin" className="flex items-center space-x-2">
            <div className="h-8 w-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-gray-900 font-bold text-sm">UP</span>
            </div>
            <span className="font-semibold">UPIM Admin</span>
          </Link>
          <button 
            className="lg:hidden text-gray-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1 pb-32">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer
                  ${isActive 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-white/5'}
                `}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-gray-800 bg-gray-900">
          <Link
            to="/"
            className="flex items-center space-x-3 px-3 py-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
          >
            <ChevronRight className="h-5 w-5 rotate-180 flex-shrink-0" />
            <span className="text-sm font-medium">Volver al sitio</span>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors mt-2 cursor-pointer"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm font-medium">Cerrar sesión</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
          <button
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex items-center space-x-4 ml-auto">
            <button className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {user?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user?.name || 'Administrador'}</p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
